import React, { useEffect, useState } from 'react';
import { resourcesAPI, resourceTypesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLocalization } from '../context/LocalizationContext';
import styles from '../styles/ResourcesPage.module.css';

const TYPE_PALETTE = [
  { accent: '#9bb9ff', soft: '#f4f8ff', ink: '#4d72d8' },
  { accent: '#8fd7ca', soft: '#effcf8', ink: '#2a8a7a' },
  { accent: '#f3c983', soft: '#fff9ec', ink: '#b7791f' },
  { accent: '#b8a4f4', soft: '#f6f2ff', ink: '#6d55c9' },
  { accent: '#8fd2f4', soft: '#eef9ff', ink: '#2f88b8' },
  { accent: '#a7d6a1', soft: '#f2fbf0', ink: '#4c8d45' },
];

const formatTypeLabel = (value) => String(value || '')
  .split(/[_\s-]+/)
  .filter(Boolean)
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(' ');

const getResourceTypeTheme = (type, resourceTypes) => {
  const normalizedType = String(type || '').trim().toLowerCase();
  const normalizedTypes = (resourceTypes || []).map((item) => String(item).trim().toLowerCase());
  const foundIndex = normalizedTypes.indexOf(normalizedType);
  const paletteIndex = foundIndex >= 0
    ? foundIndex % TYPE_PALETTE.length
    : normalizedType.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0) % TYPE_PALETTE.length;
  return TYPE_PALETTE[paletteIndex];
};

const getResourceCardStyle = (type, resourceTypes) => {
  const theme = getResourceTypeTheme(type, resourceTypes);
  return {
    '--resource-accent': theme.accent,
    '--resource-soft': theme.soft,
    '--resource-ink': theme.ink,
  };
};

const getUtilizedTeams = (resource) => String(resource?.utilized_by_teams || '')
  .split('||')
  .map((item) => item.trim())
  .filter(Boolean);

const fmtRM = (val) => `RM ${Number(val || 0).toLocaleString('en-MY', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;

const ResourcesPage = () => {
  const { can } = useAuth();
  const { t } = useLocalization();
  const canViewFinancials = can('financials', 'view');
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [resourceTypes, setResourceTypes] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [newResource, setNewResource] = useState({
    name: '',
    type: '',
    description: '',
    quantity: 1,
    unitCost: '',
  });
  const [editResource, setEditResource] = useState({
    id: '',
    name: '',
    type: 'transport',
    description: '',
    quantity: 1,
    available: 1,
    usedQuantity: 0,
    status: 'active',
    unitCost: '',
  });

  useEffect(() => {
    fetchResources();
    fetchResourceTypes();
  }, []);

  const fetchResourceTypes = async () => {
    try {
      const response = await resourceTypesAPI.getAll();
      const types = (response.data || [])
        .filter((item) => item.status === 'active')
        .map((item) => item.name);
      setResourceTypes(types);

      if (types.length > 0) {
        setNewResource((prev) => ({ ...prev, type: prev.type || types[0] }));
        setEditResource((prev) => ({ ...prev, type: prev.type || types[0] }));
      }
    } catch (error) {
      console.error('Failed to fetch resource types:', error);
      setResourceTypes([]);
    }
  };

  const fetchResources = async () => {
    try {
      setLoading(true);
      const response = await resourcesAPI.getAll();
      setResources(response.data);
    } catch (error) {
      console.error('Failed to fetch resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredResources = filter === 'all' 
    ? resources 
    : resources.filter(r => r.type === filter);

  const handleCreateResource = async (e) => {
    e.preventDefault();
    try {
      await resourcesAPI.create(newResource);
      setNewResource({
        name: '',
        type: resourceTypes[0] || '',
        description: '',
        quantity: 1,
        unitCost: '',
      });
      setShowCreateModal(false);
      fetchResources();
    } catch (error) {
      console.error('Failed to create resource:', error);
      alert(`${t('resources_create_failed_prefix', 'Error creating resource')}: ${error.response?.data?.message || error.message}`);
    }
  };

  const openEditModal = (resource) => {
    setEditResource({
      id: resource.id,
      name: resource.name,
      type: resource.type,
      description: resource.description || '',
      quantity: resource.quantity,
      available: resource.available,
      usedQuantity: Math.max(Number(resource.quantity || 0) - Number(resource.available || 0), 0),
      status: resource.status,
      unitCost: resource.unit_cost !== undefined ? String(resource.unit_cost) : '',
    });
    setShowEditModal(true);
  };

  const handleUpdateResource = async (e) => {
    e.preventDefault();
    try {
      await resourcesAPI.update(editResource.id, {
        id: editResource.id,
        name: editResource.name,
        type: editResource.type,
        description: editResource.description,
        quantity: editResource.quantity,
        status: editResource.status,
        unitCost: editResource.unitCost,
      });
      setShowEditModal(false);
      fetchResources();
    } catch (error) {
      alert(error.response?.data?.message || t('resources_update_failed', 'Failed to update resource'));
    }
  };

  const getDerivedAvailableQuantity = (resource) => {
    const totalQuantity = Number(resource.quantity || 0);
    const usedQuantity = Number(resource.usedQuantity || 0);
    return Math.max(totalQuantity - usedQuantity, 0);
  };

  const handleDeleteResource = async (resourceId, name) => {
    if (!window.confirm(`${t('resources_delete_confirm_prefix', 'Delete resource')} ${name}?`)) return;
    try {
      await resourcesAPI.delete(resourceId);
      fetchResources();
    } catch (error) {
      alert(error.response?.data?.message || t('resources_delete_failed', 'Failed to delete resource'));
    }
  };

  const statusLabelMap = {
    active: t('common_status_active', 'Active'),
    inactive: t('common_status_inactive', 'Inactive'),
    maintenance: t('resources_status_maintenance', 'Maintenance'),
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>{t('resources_page_title', 'Resources Management')}</h1>
        {can('resources', 'create') && (
          <button className={styles.createBtn} onClick={() => setShowCreateModal(true)}>{t('resources_add_resource', '+ Add Resource')}</button>
        )}
      </div>

      <div className={styles.filters}>
        <button 
          className={`${styles.filterBtn} ${filter === 'all' ? styles.active : ''}`}
          onClick={() => setFilter('all')}
        >
          {t('resources_filter_all', 'All')}
        </button>
        {resourceTypes.map(type => (
          <button
            key={type}
            className={`${styles.filterBtn} ${filter === type ? styles.active : ''}`}
            onClick={() => setFilter(type)}
          >
            {type.charAt(0).toUpperCase() + type.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <p>{t('resources_loading', 'Loading resources...')}</p>
      ) : (
        <div className={styles.resourcesGrid}>
          {filteredResources.map((resource) => (
            (() => {
              const utilizedTeams = getUtilizedTeams(resource);
              return (
            <div
              key={resource.id}
              className={styles.resourceCard}
              style={getResourceCardStyle(resource.type, resourceTypes)}
            >
              <span className={styles.typePill}>{formatTypeLabel(resource.type)}</span>
              <div className={styles.cardTopRow}>
                <span className={`${styles.statusBadge} ${styles[`statusBadge_${resource.status}`]}`}>
                  {statusLabelMap[resource.status] || resource.status}
                </span>
              </div>
              <div className={styles.cardHeadingRow}>
                <h3>{resource.name}</h3>
                <div className={styles.quantitySummary}>
                  <div className={styles.quantityChip}>
                    <span className={styles.quantityValue}>{resource.quantity}</span>
                    <span className={styles.quantityLabel}>{t('reports_total', 'Total')}</span>
                  </div>
                  <div className={styles.quantityChip}>
                    <span className={styles.quantityValue}>{resource.available}</span>
                    <span className={styles.quantityLabel}>{t('tasks_available', 'available')}</span>
                  </div>
                </div>
              </div>
              {canViewFinancials && Number(resource.unit_cost) > 0 && (
                <div className={styles.unitCostBadge}>
                  {fmtRM(resource.unit_cost)} / {t('resources_unit', 'unit')}
                </div>
              )}
              <p className={styles.description}>{resource.description}</p>
              <div className={styles.utilizationBlock}>
                <div className={styles.utilizationTitle}>{t('resources_utilized_by_tasks', 'Utilized By Tasks')}</div>
                {utilizedTeams.length === 0 ? (
                  <div className={styles.utilizationEmpty}>{t('resources_not_assigned_active_task', 'Not assigned to any active task')}</div>
                ) : (
                  <div className={styles.utilizationList}>
                    {utilizedTeams.map((team) => (
                      <span key={`${resource.id}-${team}`} className={styles.utilizationItem}>{team}</span>
                    ))}
                  </div>
                )}
              </div>
              {(can('resources', 'edit') || can('resources', 'delete')) && (
                <div className={styles.resourceActions}>
                  {can('resources', 'edit') && (
                    <button className={styles.editBtn} onClick={() => openEditModal(resource)}>{t('teams_edit', 'Edit')}</button>
                  )}
                  {can('resources', 'delete') && (
                    <button
                      className={styles.deleteBtn}
                      onClick={() => handleDeleteResource(resource.id, resource.name)}
                    >
                      {t('teams_delete', 'Delete')}
                    </button>
                  )}
                </div>
              )}
            </div>
              );
            })()
          ))}
        </div>
      )}

      {showCreateModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h2>{t('resources_modal_add_title', 'Add New Resource')}</h2>
            <form onSubmit={handleCreateResource}>
              <div className={styles.formGroup}>
                <label>{t('resources_name_required', 'Resource Name *')}</label>
                <input
                  type="text"
                  required
                  value={newResource.name}
                  onChange={(e) => setNewResource({ ...newResource, name: e.target.value })}
                  placeholder={t('resources_name_placeholder', 'Enter resource name')}
                />
              </div>
              <div className={styles.formGroup}>
                <label>{t('resources_type_required', 'Type *')}</label>
                <select
                  required
                  value={newResource.type}
                  onChange={(e) => setNewResource({ ...newResource, type: e.target.value })}
                >
                  {resourceTypes.length === 0 && <option value="">{t('resources_no_type_available', 'No type available')}</option>}
                  {resourceTypes.map(type => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>{t('tasks_label_description', 'Description')}</label>
                <textarea
                  value={newResource.description}
                  onChange={(e) => setNewResource({ ...newResource, description: e.target.value })}
                  placeholder={t('resources_description_placeholder', 'Enter resource description')}
                />
              </div>
              <div className={styles.formGroup}>
                <label>{t('resources_quantity_required', 'Quantity *')}</label>
                <input
                  type="number"
                  required
                  min="1"
                  value={newResource.quantity}
                  onChange={(e) => setNewResource({ ...newResource, quantity: parseInt(e.target.value) })}
                />
              </div>
              {canViewFinancials && (
                <div className={styles.formGroup}>
                  <label>{t('resources_unit_cost_label', 'Unit Cost (RM per unit per task)')}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder={t('common_amount_placeholder', '0.00')}
                    value={newResource.unitCost}
                    onChange={(e) => setNewResource({ ...newResource, unitCost: e.target.value })}
                  />
                </div>
              )}
              <div className={styles.modalButtons}>
                <button type="submit" className={styles.submitBtn}>{t('resources_add_resource_submit', 'Add Resource')}</button>
                <button type="button" className={styles.cancelBtn} onClick={() => setShowCreateModal(false)}>{t('common_cancel', 'Cancel')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h2>{t('resources_modal_edit_title', 'Edit Resource')}</h2>
            <form onSubmit={handleUpdateResource}>
              <div className={styles.formGroup}>
                <label>{t('resources_name_required', 'Resource Name *')}</label>
                <input
                  type="text"
                  required
                  value={editResource.name}
                  onChange={(e) => setEditResource({ ...editResource, name: e.target.value })}
                />
              </div>
              <div className={styles.formGroup}>
                <label>{t('resources_type_required', 'Type *')}</label>
                <select
                  required
                  value={editResource.type}
                  onChange={(e) => setEditResource({ ...editResource, type: e.target.value })}
                >
                  {resourceTypes.length === 0 && <option value="">{t('resources_no_type_available', 'No type available')}</option>}
                  {resourceTypes.map(type => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>{t('tasks_label_description', 'Description')}</label>
                <textarea
                  value={editResource.description}
                  onChange={(e) => setEditResource({ ...editResource, description: e.target.value })}
                />
              </div>
              <div className={styles.formGroup}>
                <label>{t('resources_total_quantity_required', 'Total Quantity *')}</label>
                <input
                  type="number"
                  required
                  value={editResource.quantity}
                  min={Math.max(Number(editResource.usedQuantity || 0), 1)}
                  onChange={(e) => setEditResource({ ...editResource, quantity: Number(e.target.value) })}
                />
              </div>
              <div className={styles.formGroup}>
                <label>{t('resources_available_quantity', 'Available Quantity')}</label>
                <input
                  type="number"
                  min="0"
                  value={getDerivedAvailableQuantity(editResource)}
                  readOnly
                  disabled
                />
                <p className={styles.helperText}>
                  {t('resources_available_quantity_helper_prefix', 'Calculated automatically from total quantity minus in-use quantity')} ({editResource.usedQuantity}).
                </p>
              </div>
              <div className={styles.formGroup}>
                <label>{t('users_status_required', 'Status *')}</label>
                <select
                  value={editResource.status}
                  onChange={(e) => setEditResource({ ...editResource, status: e.target.value })}
                >
                  <option value="active">{t('common_status_active', 'Active')}</option>
                  <option value="inactive">{t('common_status_inactive', 'Inactive')}</option>
                  <option value="maintenance">{t('resources_status_maintenance', 'Maintenance')}</option>
                </select>
              </div>
              {canViewFinancials && (
                <div className={styles.formGroup}>
                  <label>{t('resources_unit_cost_label', 'Unit Cost (RM per unit per task)')}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder={t('common_amount_placeholder', '0.00')}
                    value={editResource.unitCost}
                    onChange={(e) => setEditResource({ ...editResource, unitCost: e.target.value })}
                  />
                </div>
              )}
              <div className={styles.modalButtons}>
                <button type="submit" className={styles.submitBtn}>{t('common_save', 'Save')}</button>
                <button type="button" className={styles.cancelBtn} onClick={() => setShowEditModal(false)}>{t('common_cancel', 'Cancel')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourcesPage;
